'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:iframe');

getDependencyClass('phoenix-page').then((pageClass) => {
    const DELAY_TIMEOUT = 30000;

    /**
     * <phoenix-iframe> Web Component.
     *
     * Add lazy-loading and GDPR compliance capablities to iframes.
     * Mainly used to load TML external provider embeds.
     *
     * The following attributes are propagated to the dropped iframe tag:
     *   allowfullscreen, allowtransparency, frameborder, height, mozallowfullscreen,
     *   name, playsinline, sandbox, scrolling, seamless, src, srcdoc,
     *   webkitallowfullscreen, width.
     *
     * 'id', 'class', 'style' and 'title' can also be set on the dropped iframe using
     * 'iframe-id', 'iframe-class', 'iframe-style' and 'iframe-title' respectively.
     *
     * Lazy-loading is implemented using InteresectionObserver and is enabled
     * by the boolean attribute lazy-load.
     *
     * The boolean attribute block-on-gdpr-applicable simply causes the iframe to not be
     * rendered if GDPR is applicable. (more options will come in the future)
     *
     * @example
     *   <phoenix-iframe
     *     src="https://example.com/"
     *     width="400"
     *     height="300"
     *     iframe-class="m-video"
     *     lazy-load
     *     block-on-gdpr-applicable
     *   >
     *     <div data-fallback hidden>
     *        <a href="...">click here</a>
     *     </div>
     *   </phoenix-iframe>
     */
    class PhoenixIframe extends pageClass.PhoenixElement {
        connectedCallback() {
            this.lazyLoad = this.hasAttribute('lazy-load');
            this.delayLoad = this.hasAttribute('delay-load');
            this.setAttribute(
                'style',
                'position: absolute; display: block; top: 0; max-width: 1100px; width: 100%; bottom: 0;'
            );

            // Cascade through Gdpr, delay load, lazy load, and render iframe.
            this.canRender().then((canRender) => {
                if (canRender) {
                    this._handleDelayLoad();
                } else {
                    // Make CSS show the fallback markup
                    this.classList.add('mm-custom-element--fallback');
                }
            });
        }

        /**
         * Handle delay loading, and cascade to lazy loading.
         */
        _handleDelayLoad() {
            if (this.delayLoad) {
                this._whenPageLoaded(DELAY_TIMEOUT).then(() => {
                    this._handleLazyLoad();
                });
            } else {
                this._handleLazyLoad();
            }
        }

        /**
         * Handle lazy loading, and cascade to render the iframe.
         */
        _handleLazyLoad() {
            if (this.lazyLoad) {
                // Use an intersection observer to know when to render.
                this.observeFirstIntersection();
            } else {
                // Render immediately.
                this._renderIframe();
            }
        }

        /**
         * Resolve on the window load event, or if the window load event has
         * already occured, or after a timeout.
         */
        _whenPageLoaded(timeoutMs) {
            if (document.readyState === 'complete') {
                return Promise.resolve();
            } else {
                if (!this.delayLoadTimeouts) {
                    this.delayLoadTimeouts = [];
                }
                window.addEventListener('load', () => {
                    for (var i in this.delayLoadTimeouts) {
                        window.clearTimeout(this.delayLoadTimeouts[i]);
                    }
                });
                return new Promise((resolve, reject) => {
                    window.addEventListener('load', resolve);
                    this.delayLoadTimeouts.push(setTimeout(resolve, timeoutMs || 0));
                });
            }
        }

        /**
         * DOM manipulations to render the iframe.
         */
        _renderIframe() {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('style', 'height: 100%; width: 100%;');

            // propagate iframe attributes
            [
                'allow',
                'allowfullscreen',
                'allowtransparency',
                'data-playlist', // we could potentially propagate all data-* attributes
                'frameborder',
                'height',
                'mozallowfullscreen',
                'name',
                'playsinline',
                'sandbox',
                'scrolling',
                'seamless',
                'src',
                'srcdoc',
                'webkitallowfullscreen',
                'width',
            ].forEach((attrName) => {
                if (this.hasAttribute(attrName)) {
                    iframe.setAttribute(attrName, this.getAttribute(attrName));
                }
            });

            // propagate id, class, style and title
            const iframeId = this.getAttribute('iframe-id');
            if (this.hasAttribute('iframe-id')) {
                iframe.setAttribute('id', iframeId);
            }
            if (this.hasAttribute('iframe-class')) {
                iframe.setAttribute('class', this.getAttribute('iframe-class'));
            }

            if (this.hasAttribute('iframe-title')) {
                iframe.setAttribute('title', this.getAttribute('iframe-title'));
            }

            this.appendChild(iframe);

            // Implemented for compatability with iframeResizer.
            this.emitEvent('phoenix:iframe:render', { detail: { elem: iframe }, bubbles: true });
        }

        onFirstIntersection() {
            this._renderIframe();
        }
    }
    window.customElements.define('phoenix-iframe', PhoenixIframe);
    measure.end();
});
